<template>
  <div>
    <div ref="mainTarget">
      <slot name="main" />
    </div>
    <slot name="out-of-viewport" :show="enableIntersectionObserver && show" />
  </div>
</template>

<script setup lang="ts" generic="T">
import { ref, watch, useTemplateRef } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';

type ScrollDirection = 'up' | 'down' | 'both';

type LazyLoadWhenOutOfViewportProps = {
  scrollDirection?: ScrollDirection; // Direction of scrolling that triggers the "out-of-viewport" visibility.
  enableIntersectionObserver?: boolean;
  forceShow?: T;
};

const { scrollDirection = 'both', enableIntersectionObserver = true, forceShow = null } = defineProps<LazyLoadWhenOutOfViewportProps>();

const mainTargetRef = useTemplateRef<HTMLElement | null>('mainTarget');
const show = ref(false);

if (enableIntersectionObserver) {
  useIntersectionObserver(
    mainTargetRef,
    ([entry]) => {
      const { isIntersecting, boundingClientRect } = entry;

      if (isIntersecting) {
        // main target in viewport -> hide out-of-viewport slotted item
        show.value = false;
      } else if (scrollDirection === 'both') {
        // main target out of viewport -> scrolling up or down -> show out-of-viewport slotted item
        show.value = true;
      } else if (scrollDirection === 'up' && boundingClientRect.top > 0) {
        // main target out of viewport -> scrolling up -> show out-of-viewport slotted item
        show.value = true;
      } else if (scrollDirection === 'down' && boundingClientRect.top < 0) {
        // main target out of viewport -> scrolling down -> show out-of-viewport slotted item
        show.value = true;
      } else {
        // fallback -> hide out-of-viewport slotted item
        show.value = false;
      }
    },
    {
      threshold: 0,
    },
  );

  watch(
    () => forceShow,
    () => {
      show.value = true;
    },
  );
}
</script>
